// ForgeRecruitment — Stages 2–8 analytics: pipeline funnel, persona progression,
// loss analysis, and per-stage drill-down. Analytical counterpart to ApplicantTracker.

import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  usePipelineWaterfall,
  usePersonaBreakdown,
  useStageDetail,
  useBenchmarks,
} from "../../hooks/useForge";
import type { WaterfallStepRow } from "../../hooks/useForge";
import { useApplicants, useAggregates } from "../../hooks/useDashboard";
import type { ApplicantRow } from "../../hooks/useDashboard";
import KPICard from "../../components/dashboard/KPICard";
import ChartCard from "../../components/dashboard/ChartCard";
import StatusBadge from "../../components/dashboard/StatusBadge";
import { LiveIndicator } from "../../components/shared/LiveIndicator";
import { PERSONAS, NAMED_PERSONAS } from "../../lib/personas";
import type { Persona } from "../../lib/personas";
import { useTheme } from "../../hooks/useTheme";
import { getChartTheme } from "../../lib/chart-theme";
import BenchmarkIndicator from "../../components/dashboard/BenchmarkIndicator";

// ── Constants ──────────────────────────────────────────────────────────────────

const DCVFD_ACCENT = "#0E403A";

// Pipeline stages 2-8 labels (recruitment phase)
const RECRUITMENT_STAGE_NUMS = [2, 3, 4, 5, 6, 7, 8];

// Days target for recruitment pipeline
const TARGET_DAYS = 45;

// ── Helpers ───────────────────────────────────────────────────────────────────

function asNumber(v: unknown, fallback = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function asString(v: unknown, fallback = ""): string {
  if (typeof v === "string") return v;
  return fallback;
}

function _applicantName(row: ApplicantRow): string {
  const first = asString(row.first_name);
  const last = asString(row.last_name);
  if (first || last) return `${first} ${last}`.trim();
  return asString(row.applicant_name_id) || "Unknown";
}

function isCompleteStage(stage: string | null | undefined): boolean {
  if (!stage) return false;
  const s = stage.toLowerCase();
  return (
    s.includes("bod") ||
    s.includes("approved") ||
    s.includes("complete") ||
    s.includes("member")
  );
}

function isArchivedResult(row: ApplicantRow): boolean {
  const result = asString(
    (row as Record<string, unknown>)["application_result"] ??
      (row as Record<string, unknown>)["Application_Result"],
  );
  const stage = asString(row.pipeline_stage);
  return (
    result.toLowerCase().includes("archive") ||
    stage.toLowerCase().includes("archive") ||
    stage.toLowerCase().includes("withdrew") ||
    stage.toLowerCase().includes("rejected")
  );
}

// RefreshDot removed — using shared LiveIndicator component

// ── Persona chip ──────────────────────────────────────────────────────────────

function _PersonaChip({ persona }: { persona: Persona }) {
  const p = PERSONAS[persona];
  if (persona === "unknown") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-500/15 text-zinc-500">
        Unknown
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: `${p.color}20`, color: p.color }}
    >
      {p.label.split(" ")[0]}
    </span>
  );
}

// ── Stage funnel bar tooltip ──────────────────────────────────────────────────

interface FunnelTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: WaterfallStepRow & { isWorstDrop: boolean } }>;
}

function FunnelTooltip({ active, payload }: FunnelTooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="glass rounded-lg px-3 py-2.5 text-xs shadow-lg min-w-[160px]">
      <p className="font-semibold text-theme-primary mb-1">{d.name}</p>
      <div className="flex justify-between gap-4">
        <span className="text-theme-muted">At stage</span>
        <span className="font-bold tabular-nums text-theme-primary">
          {d.count}
        </span>
      </div>
      {d.dropCount !== null && d.dropCount > 0 && (
        <div className="flex justify-between gap-4 mt-1">
          <span
            className={
              d.isWorstDrop
                ? "text-red-600 dark:text-red-400 font-semibold"
                : "text-theme-muted"
            }
          >
            Drop-off
          </span>
          <span
            className={`font-bold tabular-nums ${d.isWorstDrop ? "text-red-600 dark:text-red-400" : "text-theme-secondary"}`}
          >
            -{d.dropCount} (
            {d.dropPct != null ? `${d.dropPct.toFixed(0)}%` : "—"})
          </span>
        </div>
      )}
      {d.avgDaysInStage !== undefined && d.avgDaysInStage !== null && (
        <div className="flex justify-between gap-4 mt-1">
          <span className="text-theme-muted">Avg days</span>
          <span className="font-bold tabular-nums text-theme-secondary">
            {d.avgDaysInStage}d
          </span>
        </div>
      )}
    </div>
  );
}

// ── Stage Funnel section ──────────────────────────────────────────────────────

function StageFunnel({
  steps,
  isLoading,
}: {
  steps: WaterfallStepRow[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const chart = getChartTheme(resolvedTheme === "dark");
  const recruitmentSteps = steps.filter((s) =>
    RECRUITMENT_STAGE_NUMS.includes(s.step),
  );
  const maxCount = recruitmentSteps.reduce((m, s) => Math.max(m, s.count), 0);
  const worstDropIdx = recruitmentSteps.reduce((worst, s, i, arr) => {
    const worstDrop = arr[worst]?.dropCount ?? 0;
    const thisDrop = s.dropCount ?? 0;
    return thisDrop > worstDrop ? i : worst;
  }, 0);
  const chartData = recruitmentSteps.map((s, i) => ({
    ...s,
    isWorstDrop: i === worstDropIdx,
  }));

  if (isLoading) {
    return (
      <div className="flex items-end gap-2 h-56 px-4">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex-1 flex flex-col gap-1 items-center">
            <div
              className="skeleton w-full rounded-t"
              style={{ height: `${50 + ((i * 29 + 13) % 50)}%` }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (recruitmentSteps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <p className="text-sm text-theme-muted">No stage data available</p>
        <p className="text-xs text-theme-muted opacity-60">
          Pipeline waterfall data will appear after sync
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ResponsiveContainer width="100%" height={240}>
        <BarChart
          data={chartData}
          margin={{ top: 8, right: 8, left: -10, bottom: 4 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={chart.gridStroke}
            vertical={false}
          />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: chart.tickColor }}
            axisLine={false}
            tickLine={false}
            interval={0}
            angle={-30}
            textAnchor="end"
            height={52}
          />
          <YAxis
            tick={{ fontSize: 11, fill: chart.tickColor }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip
            content={<FunnelTooltip />}
            cursor={{ fill: chart.cursorFill }}
          />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={56}>
            {chartData.map((entry, i) => (
              <Cell
                key={`cell-${i}`}
                fill={entry.isWorstDrop ? "#dc2626" : DCVFD_ACCENT}
                fillOpacity={
                  entry.isWorstDrop
                    ? 0.9
                    : 0.75 + (entry.count / (maxCount || 1)) * 0.25
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Stage table with drop-off detail */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-theme-subtle">
              <th className="px-3 py-2 text-theme-muted font-semibold uppercase tracking-wider">
                Stage
              </th>
              <th className="px-3 py-2 text-theme-muted font-semibold uppercase tracking-wider text-right">
                Count
              </th>
              <th className="px-3 py-2 text-theme-muted font-semibold uppercase tracking-wider text-right">
                Drop
              </th>
              <th className="px-3 py-2 text-theme-muted font-semibold uppercase tracking-wider text-right">
                Drop%
              </th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((s) => (
              <tr
                key={s.step}
                className="hover:bg-theme-overlay/50 transition-colors"
              >
                <td className="px-3 py-2 font-medium text-theme-primary">
                  {s.name}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-theme-secondary">
                  {s.count}
                </td>
                <td
                  className={`px-3 py-2 text-right tabular-nums font-semibold ${s.isWorstDrop ? "text-red-600 dark:text-red-400" : "text-theme-muted"}`}
                >
                  {s.dropCount != null && s.dropCount > 0
                    ? `-${s.dropCount}`
                    : "—"}
                </td>
                <td
                  className={`px-3 py-2 text-right tabular-nums ${s.isWorstDrop ? "text-red-600 dark:text-red-400 font-semibold" : "text-theme-muted"}`}
                >
                  {s.dropPct != null && s.dropPct > 0
                    ? `${s.dropPct.toFixed(0)}%`
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Persona Progression section ───────────────────────────────────────────────

interface PersonaProgressionProps {
  stages: Array<{
    stage: string;
    personas: Record<string, number>;
    total: number;
  }>;
  isLoading: boolean;
}

function PersonaProgression({ stages, isLoading }: PersonaProgressionProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton h-16 rounded-xl" />
        ))}
      </div>
    );
  }

  if (stages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <p className="text-sm text-theme-muted">
          No persona breakdown available
        </p>
        <p className="text-xs text-theme-muted opacity-60">
          Requires DOB data on applicant records
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        {NAMED_PERSONAS.map((p) => (
          <div key={p} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: PERSONAS[p].color }}
            />
            <span className="text-xs text-theme-muted">
              {PERSONAS[p].label.split(" ")[0]}
            </span>
          </div>
        ))}
      </div>

      {/* Stage × persona grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-theme-subtle">
              <th className="px-3 py-2 text-left text-theme-muted font-semibold uppercase tracking-wider">
                Stage
              </th>
              {NAMED_PERSONAS.map((p) => (
                <th
                  key={p}
                  className="px-3 py-2 text-right text-theme-muted font-semibold uppercase tracking-wider"
                  style={{ color: PERSONAS[p].color }}
                >
                  {PERSONAS[p].label.split(" ")[0]}
                </th>
              ))}
              <th className="px-3 py-2 text-right text-theme-muted font-semibold uppercase tracking-wider">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {stages.map((stage) => (
              <tr
                key={stage.stage}
                className="hover:bg-theme-overlay/50 transition-colors"
              >
                <td className="px-3 py-2 font-medium text-theme-primary">
                  {stage.stage}
                </td>
                {NAMED_PERSONAS.map((p) => {
                  const count = stage.personas[p] ?? 0;
                  const pct =
                    stage.total > 0
                      ? Math.round((count / stage.total) * 100)
                      : 0;
                  return (
                    <td key={p} className="px-3 py-2 text-right tabular-nums">
                      <span
                        className={
                          count > 0
                            ? "font-semibold text-theme-primary"
                            : "text-theme-muted"
                        }
                      >
                        {count}
                      </span>
                      {count > 0 && (
                        <span className="text-theme-muted ml-1">({pct}%)</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right tabular-nums font-bold text-theme-primary">
                  {stage.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Decision timeline reference */}
      <div className="rounded-xl border border-theme-subtle bg-theme-overlay/40 p-3">
        <p className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-2">
          Decision Timeline Benchmarks
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {NAMED_PERSONAS.map((p) => {
            const profile = PERSONAS[p];
            return (
              <div key={p} className="flex flex-col gap-0.5">
                <span
                  className="text-xs font-semibold"
                  style={{ color: profile.color }}
                >
                  {profile.label.split(" ")[0]}
                </span>
                <span className="text-xs text-theme-muted">
                  {profile.decisionTimelineWeeks.min}–
                  {profile.decisionTimelineWeeks.max} wks
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Loss Analysis section ─────────────────────────────────────────────────────

interface LossItem {
  reason: string;
  count: number;
  recoverable: boolean;
}

function buildLossData(applicants: ApplicantRow[]): LossItem[] {
  const counts = new Map<string, number>();
  for (const row of applicants) {
    if (!isArchivedResult(row)) continue;
    const stage = asString(row.pipeline_stage) || "Unknown";
    counts.set(stage, (counts.get(stage) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([reason, count]) => ({
      reason,
      count,
      recoverable:
        reason.toLowerCase().includes("inactive") ||
        reason.toLowerCase().includes("pause") ||
        reason.toLowerCase().includes("withdrew"),
    }));
}

function LossAnalysis({
  applicants,
  isLoading,
}: {
  applicants: ApplicantRow[];
  isLoading: boolean;
}) {
  const lossData = useMemo(() => buildLossData(applicants), [applicants]);
  const totalLost = lossData.reduce((s, d) => s + d.count, 0);
  const recoverableCount = lossData
    .filter((d) => d.recoverable)
    .reduce((s, d) => s + d.count, 0);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton h-10 rounded-xl" />
        ))}
      </div>
    );
  }

  if (lossData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-status-green"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
        <p className="text-sm font-medium text-status-green">
          No archived applicants found
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary chips */}
      <div className="flex flex-wrap gap-3">
        <div className="glass rounded-xl px-4 py-3 flex flex-col gap-0.5">
          <span className="text-xs text-theme-muted">Total Lost</span>
          <span className="text-2xl font-bold tabular-nums text-status-red">
            {totalLost}
          </span>
        </div>
        <div className="glass rounded-xl px-4 py-3 flex flex-col gap-0.5">
          <span className="text-xs text-theme-muted">
            Potentially Recoverable
          </span>
          <span className="text-2xl font-bold tabular-nums text-status-amber">
            {recoverableCount}
          </span>
        </div>
        <div className="glass rounded-xl px-4 py-3 flex flex-col gap-0.5">
          <span className="text-xs text-theme-muted">Permanent Loss</span>
          <span className="text-2xl font-bold tabular-nums text-theme-secondary">
            {totalLost - recoverableCount}
          </span>
        </div>
      </div>

      {/* Loss by stage table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-theme-subtle">
              <th className="px-3 py-2 text-left text-theme-muted font-semibold uppercase tracking-wider">
                Stage / Reason
              </th>
              <th className="px-3 py-2 text-right text-theme-muted font-semibold uppercase tracking-wider">
                Count
              </th>
              <th className="px-3 py-2 text-right text-theme-muted font-semibold uppercase tracking-wider">
                Type
              </th>
            </tr>
          </thead>
          <tbody>
            {lossData.map((item) => (
              <tr
                key={item.reason}
                className="hover:bg-theme-overlay/50 transition-colors"
              >
                <td className="px-3 py-2 font-medium text-theme-primary">
                  {item.reason}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-status-red">
                  {item.count}
                </td>
                <td className="px-3 py-2 text-right">
                  {item.recoverable ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-700 dark:text-amber-400">
                      Recoverable
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-500/15 text-zinc-600 dark:text-zinc-400">
                      Permanent
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-theme-muted">
        Action: Re-engage recoverable applicants — follow up within 90 days for
        best conversion. Permanent losses suggest a friction point at that stage
        requiring process review.
      </p>
    </div>
  );
}

// ── Stage Detail Dropdown ─────────────────────────────────────────────────────

function StageDetailPanel({ stageNum }: { stageNum: number }) {
  const { data, isLoading } = useStageDetail(stageNum);

  if (isLoading) {
    return (
      <div className="space-y-2 mt-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton h-9 rounded-lg" />
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <p className="text-sm text-theme-muted mt-3">
        No detail data for stage {stageNum}.
      </p>
    );
  }

  return (
    <div className="mt-3 space-y-3">
      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="glass rounded-xl px-3 py-2.5">
          <p className="text-xs text-theme-muted">Currently Here</p>
          <p className="text-xl font-bold tabular-nums text-theme-primary mt-0.5">
            {data.current}
          </p>
        </div>
        <div className="glass rounded-xl px-3 py-2.5">
          <p className="text-xs text-theme-muted">Ever Reached</p>
          <p className="text-xl font-bold tabular-nums text-theme-primary mt-0.5">
            {data.reachedStage}
          </p>
        </div>
        <div className="glass rounded-xl px-3 py-2.5">
          <p className="text-xs text-theme-muted">Avg Days in Stage</p>
          <p className="text-xl font-bold tabular-nums text-theme-primary mt-0.5">
            {data.avgDaysInStage != null
              ? `${Math.round(data.avgDaysInStage)}d`
              : "—"}
          </p>
        </div>
        <div className="glass rounded-xl px-3 py-2.5">
          <p className="text-xs text-theme-muted">Transition Rate</p>
          <p
            className={`text-xl font-bold tabular-nums mt-0.5 ${data.transitionRate >= 0.7 ? "text-status-green" : data.transitionRate >= 0.4 ? "text-status-amber" : "text-status-red"}`}
          >
            {(data.transitionRate * 100).toFixed(0)}%
          </p>
        </div>
      </div>

      {/* Persona breakdown */}
      <div className="flex flex-wrap gap-2">
        {NAMED_PERSONAS.map((p) => {
          const count = data.personas[p] ?? 0;
          const profile = PERSONAS[p];
          return (
            <div
              key={p}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
              style={{
                backgroundColor: `${profile.color}18`,
                color: profile.color,
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: profile.color }}
              />
              {profile.label.split(" ")[0]}: {count}
            </div>
          );
        })}
      </div>

      {/* Projected weeks to decision by persona */}
      <div className="rounded-xl border border-theme-subtle bg-theme-overlay/40 p-3">
        <p className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-2">
          Projected Weeks to BOD Decision
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {NAMED_PERSONAS.map((p) => {
            const proj = data.projectedWeeksToDecision[p];
            const profile = PERSONAS[p];
            return (
              <div key={p} className="flex flex-col gap-0.5">
                <span
                  className="text-xs font-semibold"
                  style={{ color: profile.color }}
                >
                  {profile.label.split(" ")[0]}
                </span>
                <span className="text-xs text-theme-secondary tabular-nums">
                  {proj ? `${proj.min}–${proj.max} wks` : "—"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-theme-muted">
        Suggested action:{" "}
        {data.lossRate > 0.3
          ? `High drop-off (${(data.lossRate * 100).toFixed(0)}%) — schedule a check-in call with applicants stalled here for more than 2 weeks.`
          : data.avgDaysInStage != null && data.avgDaysInStage > 14
            ? `Avg ${Math.round(data.avgDaysInStage)} days at this stage — consider adding an automated reminder at day 7.`
            : `Stage is moving well. Monitor weekly for any stalls.`}
      </p>
    </div>
  );
}

// ── Stage selector ────────────────────────────────────────────────────────────

function StageSelector({
  steps,
  selected,
  onSelect,
}: {
  steps: WaterfallStepRow[];
  selected: number | null;
  onSelect: (n: number | null) => void;
}) {
  const recruitmentSteps = steps.filter((s) =>
    RECRUITMENT_STAGE_NUMS.includes(s.step),
  );

  return (
    <div className="flex flex-wrap gap-2">
      {recruitmentSteps.map((s) => (
        <button
          key={s.step}
          type="button"
          onClick={() => onSelect(selected === s.step ? null : s.step)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            selected === s.step
              ? "bg-[#0E403A] text-white shadow-sm"
              : "bg-theme-overlay text-theme-muted hover:text-theme-primary hover:bg-theme-overlay/80"
          }`}
        >
          {s.name}
          <span className="ml-1.5 tabular-nums opacity-70">({s.count})</span>
        </button>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ForgeRecruitment() {
  const INTERVAL = 300_000;

  const [selectedStage, setSelectedStage] = useState<number | null>(null);

  const {
    data: waterfallData,
    isLoading: waterfallLoading,
    lastUpdatedAt,
  } = usePipelineWaterfall("all", INTERVAL);
  const { data: personaBreakdownData, isLoading: personaLoading } =
    usePersonaBreakdown(INTERVAL);
  const { data: aggregatesData, isLoading: aggLoading } =
    useAggregates(INTERVAL);
  const { data: applicantsData, isLoading: applicantsLoading } = useApplicants(
    { limit: 5000 },
    INTERVAL,
  );
  const { data: benchmarksData, isLoading: benchmarksLoading } =
    useBenchmarks(300_000);

  const steps = waterfallData?.steps ?? [];
  const agg = aggregatesData?.aggregates ?? {};
  const applicants = applicantsData?.applicants ?? [];

  const _recruitmentSteps = steps.filter((s) =>
    RECRUITMENT_STAGE_NUMS.includes(s.step),
  );

  // ── KPI: Total in Recruitment — truly active: <180 days, no result, not complete ──────
  const ACTIVE_CUTOFF_DAYS = 180;
  const activeApplicants = useMemo(() => {
    return applicants.filter((row) => {
      const result = asString(
        (row as Record<string, unknown>)["application_result"] ??
          (row as Record<string, unknown>)["Application_Result"],
      );
      if (result && result.trim().length > 0) return false; // has a result → closed
      const stage = asString(row.pipeline_stage);
      if (isCompleteStage(stage)) return false; // complete stage
      const days = asNumber(row.days_since_applied);
      return days >= 0 && days <= ACTIVE_CUTOFF_DAYS;
    });
  }, [applicants]);

  const staleApplicants = useMemo(() => {
    return applicants.filter((row) => {
      const result = asString(
        (row as Record<string, unknown>)["application_result"] ??
          (row as Record<string, unknown>)["Application_Result"],
      );
      if (result && result.trim().length > 0) return false;
      const stage = asString(row.pipeline_stage).toLowerCase();
      if (isCompleteStage(asString(row.pipeline_stage))) return false;
      const days = asNumber(row.days_since_applied);
      // Stale: older than 180 days AND still early stage (New/Contacted)
      return (
        days > ACTIVE_CUTOFF_DAYS &&
        (stage.includes("new") ||
          stage.includes("contact") ||
          stage.includes("initial") ||
          stage.includes("1 -") ||
          stage.includes("2 -"))
      );
    });
  }, [applicants]);

  const totalInRecruitment = activeApplicants.length;

  // ── KPI: Avg Days in Recruitment ─ computed from active applicants ─────────
  const avgDaysInRecruitment = useMemo(() => {
    // First try aggregate key
    const fromAgg = asNumber(
      agg["avg_days_in_recruitment"] ?? agg["avg_pipeline_days"],
    );
    if (fromAgg > 0) return Math.round(fromAgg);
    // Compute client-side from active applicants
    const vals = activeApplicants
      .map((row) => asNumber(row.days_since_applied))
      .filter((v) => v > 0);
    if (vals.length === 0) return null;
    return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
  }, [agg, activeApplicants]);

  const avgDaysAccent: "green" | "amber" | "red" | "neutral" =
    avgDaysInRecruitment === null
      ? "neutral"
      : avgDaysInRecruitment <= TARGET_DAYS
        ? "green"
        : avgDaysInRecruitment <= TARGET_DAYS * 1.5
          ? "amber"
          : "red";

  // ── KPI: Conversion Rate ─────────────────────────────────────────────────────
  // Keep numerator + denominator in the same time window. bod_approved_total
  // is all-time but we fall back to bod_total_reached as denominator — both
  // are historical so the ratio is honest even when YTD data is thin. Never
  // mix applicant_count_ytd with bod_approved_total (that produced >100%
  // readings of ~173% early in the year).
  const bodTotalReached = asNumber(agg["bod_total_reached"]);
  const bodApprovedAllTime = asNumber(agg["bod_approved_total"]);
  const conversionRate =
    bodTotalReached > 0 ? (bodApprovedAllTime / bodTotalReached) * 100 : 0;

  // Biggest-funnel-loss KPI now pulls from dash_aggregates.drop_off_before_ais
  // (sourced by /api/forge/drop-off). The old 'biggestBottleneck' picked the
  // max waterfall dropCount, which gave a spurious 'BOD Vote / Motion' answer
  // — that drop was a data-linkage gap between pre-2023 BOD records and the
  // training tracker, not actual member loss.

  // ── Persona breakdown: filter to recruitment stages ──────────────────────────
  const personaStages = useMemo(() => {
    if (!personaBreakdownData?.stages) return [];
    return personaBreakdownData.stages.filter((s) => {
      // Try to match stage names to recruitment phase
      const lower = s.stage.toLowerCase();
      return (
        lower.includes("initial") ||
        lower.includes("contact") ||
        lower.includes("interview") ||
        lower.includes("background") ||
        lower.includes("medical") ||
        lower.includes("bod") ||
        lower.includes("application") ||
        lower.includes("review")
      );
    });
  }, [personaBreakdownData]);

  // ── BOD Stage Breakdown — explains where applicants who reached BOD went ─────
  // Addresses the question: "What happened to the ~900+ people who didn't make
  // it from BOD to training?"
  const bodBreakdown = useMemo(() => {
    // Applicants who reached stage 8 (BOD Vote) — identified by pipeline_stage containing "bod" or "8"
    const bodApplicantList = applicants.filter((row) => {
      const stage = asString(row.pipeline_stage).toLowerCase();
      return stage.includes("bod") || stage.match(/^8\s*[-–]/);
    });

    const resultCounts = new Map<string, number>();
    for (const row of bodApplicantList) {
      const result =
        asString(
          (row as Record<string, unknown>)["application_result"] ??
            (row as Record<string, unknown>)["Application_Result"],
        ).trim() || "Unknown / Pending";
      resultCounts.set(result, (resultCounts.get(result) ?? 0) + 1);
    }

    const breakdown = Array.from(resultCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([result, count]) => ({
        result,
        count,
        isApproved:
          result.toLowerCase().includes("approv") &&
          !result.toLowerCase().includes("not"),
        isDeclined:
          result.toLowerCase().includes("declin") ||
          result.toLowerCase().includes("denied") ||
          result.toLowerCase().includes("not approv"),
        isWithdrew: result.toLowerCase().includes("withdr"),
        isArchived: result.toLowerCase().includes("archiv"),
        isPhysical: result.toLowerCase().includes("physical"),
      }));

    const total = bodApplicantList.length;
    const approvedCount = breakdown
      .filter((b) => b.isApproved)
      .reduce((s, b) => s + b.count, 0);

    // How many approved members have a training record?
    const inTraining = applicants.filter((row) => {
      const stage = asString(row.pipeline_stage).toLowerCase();
      return (
        stage.includes("train") || stage.match(/^(9|1[0-9]|2[0-6])\s*[-–]/)
      );
    }).length;

    return { breakdown, total, approvedCount, inTraining };
  }, [applicants]);

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            Recruitment Pipeline
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Stages 2–8 analytics — from initial contact through BOD vote.
            Analytical deep-dive, not the kanban tracker.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <LiveIndicator lastUpdatedAt={lastUpdatedAt} />
        </div>
      </div>

      {/* ── KPI Row ───────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Pipeline Health
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KPICard
            label="Active in Recruitment"
            value={applicantsLoading ? "—" : totalInRecruitment}
            subtitle={
              staleApplicants.length > 0
                ? `${staleApplicants.length} stale (>180d) · <180d, no result`
                : "applied <180 days ago, no result"
            }
            accent="neutral"
            isLoading={applicantsLoading}
          />

          <KPICard
            label="Avg Days in Recruitment"
            value={
              applicantsLoading
                ? "—"
                : avgDaysInRecruitment === null
                  ? "No data"
                  : `${avgDaysInRecruitment}d`
            }
            subtitle={`target: ≤${TARGET_DAYS} days`}
            accent={avgDaysAccent}
            isLoading={applicantsLoading}
            trend={
              !applicantsLoading && avgDaysInRecruitment !== null
                ? {
                    direction:
                      avgDaysInRecruitment <= TARGET_DAYS ? "down" : "up",
                    label:
                      avgDaysInRecruitment <= TARGET_DAYS
                        ? "On target"
                        : `+${avgDaysInRecruitment - TARGET_DAYS}d over`,
                    positiveIsGood: false,
                  }
                : undefined
            }
          />

          <KPICard
            label="Conversion Rate"
            value={
              aggLoading
                ? "—"
                : bodTotalReached > 0
                  ? `${conversionRate.toFixed(1)}%`
                  : "—"
            }
            subtitle="BOD approved / BOD reached (all-time)"
            accent={
              conversionRate === 0
                ? "neutral"
                : conversionRate >= 30
                  ? "green"
                  : conversionRate >= 15
                    ? "amber"
                    : "red"
            }
            isLoading={aggLoading}
            trend={
              !aggLoading && conversionRate > 0
                ? {
                    direction: conversionRate >= 30 ? "up" : "down",
                    label:
                      conversionRate >= 30 ? "On target" : "Below 30% target",
                    positiveIsGood: true,
                  }
                : undefined
            }
          />

          <KPICard
            label="Biggest Funnel Loss"
            // Pulled from the honest drop-off analysis (see /forge Drop-Off
            // Funnel section). "Dropped before AIS" is the actual largest
            // loss cohort — ~1,643 applicants who never make it to their
            // first Applicant Information Session. The waterfall
            // 'Highest Loss Stage' was an artifact of pre-2023 BOD data
            // not being linked to the training tracker, so "lost between
            // BOD and training" was a recording gap, not attrition.
            value={
              asNumber(agg["drop_off_before_ais"]) > 0
                ? `${asNumber(agg["drop_off_before_ais"]).toLocaleString()} lost`
                : "—"
            }
            subtitle="Dropped before AIS — see Drop-Off Funnel on FORGE overview"
            accent={
              asNumber(agg["drop_off_before_ais"]) > 0 ? "red" : "neutral"
            }
            isLoading={aggLoading}
          />
        </div>

        {/* Days to BOD benchmark indicator */}
        {(() => {
          const bodBench = benchmarksData?.benchmarks?.find(
            (b) => b.metric === "avg_days_to_bod",
          );
          if (!bodBench || bodBench.currentValue === null) return null;
          return (
            <div className="mt-4">
              <BenchmarkIndicator
                current={bodBench.currentValue}
                nationalAverage={bodBench.nationalAverage}
                topQuartile={bodBench.topQuartile}
                unit="days"
                direction="lower-better"
                label={bodBench.label}
                source={bodBench.source}
                note={bodBench.note}
                isLoading={benchmarksLoading}
              />
            </div>
          );
        })()}
      </section>

      {/* ── Section 1: Stage Funnel ───────────────────────────────────── */}
      <section>
        <ChartCard
          title="Stages 2–8 Funnel"
          subtitle="Applicant count at each recruitment stage — red bar = highest drop-off point"
          headerRight={
            waterfallLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : waterfallData ? (
              <StatusBadge label={waterfallData.periodLabel} variant="muted" />
            ) : null
          }
          minHeight={300}
        >
          <StageFunnel steps={steps} isLoading={waterfallLoading} />
        </ChartCard>
      </section>

      {/* ── Section 2: Persona Progression ───────────────────────────── */}
      <section>
        <ChartCard
          title="Persona Progression"
          subtitle="Distribution of Gen Z / Millennial / Gen X / Boomer applicants across recruitment stages"
          minHeight={240}
        >
          <PersonaProgression
            stages={
              personaStages.length > 0
                ? personaStages
                : (personaBreakdownData?.stages ?? [])
            }
            isLoading={personaLoading}
          />
        </ChartCard>
      </section>

      {/* ── Section 3: Loss Analysis ──────────────────────────────────── */}
      <section>
        <ChartCard
          title="Loss Analysis"
          subtitle="Archived / withdrawn applicants by stage — recoverable vs. permanent losses"
          headerRight={
            !applicantsLoading && applicantsData ? (
              <StatusBadge
                label={`${applicantsData.count.toLocaleString()} total`}
                variant="muted"
              />
            ) : null
          }
          minHeight={240}
        >
          <LossAnalysis applicants={applicants} isLoading={applicantsLoading} />
        </ChartCard>
      </section>

      {/* ── Section 3b: BOD Stage Breakdown — what happened after BOD? ── */}
      <section>
        <ChartCard
          title="BOD Stage Breakdown"
          subtitle="Where did the applicants who reached the BOD Vote stage end up? Explains the large drop before Training."
          headerRight={
            !applicantsLoading && bodBreakdown.total > 0 ? (
              <StatusBadge
                label={`${bodBreakdown.total} reached BOD`}
                variant="muted"
              />
            ) : null
          }
          minHeight={200}
        >
          {applicantsLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="skeleton h-9 rounded-xl" />
              ))}
            </div>
          ) : bodBreakdown.total === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 gap-2">
              <p className="text-sm text-theme-muted">
                No BOD-stage applicants found
              </p>
              <p className="text-xs text-theme-muted opacity-60">
                Stage name matching: pipeline_stage contains "bod" or starts
                with "8 –"
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Summary stat row */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="glass rounded-xl px-4 py-3">
                  <p className="text-xs text-theme-muted">Reached BOD Stage</p>
                  <p className="text-2xl font-bold tabular-nums text-theme-primary mt-0.5">
                    {bodBreakdown.total.toLocaleString()}
                  </p>
                </div>
                <div className="glass rounded-xl px-4 py-3">
                  <p className="text-xs text-theme-muted">BOD Approved</p>
                  <p className="text-2xl font-bold tabular-nums text-status-green mt-0.5">
                    {bodBreakdown.approvedCount.toLocaleString()}
                  </p>
                </div>
                <div className="glass rounded-xl px-4 py-3">
                  <p className="text-xs text-theme-muted">
                    Approved → Training
                  </p>
                  <p className="text-2xl font-bold tabular-nums text-amber-500 mt-0.5">
                    {bodBreakdown.inTraining.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Result breakdown table */}
              <div>
                <p className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-2">
                  Application Result Breakdown at BOD Stage
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-theme-subtle">
                        <th className="px-3 py-2 text-left text-theme-muted font-semibold uppercase tracking-wider">
                          Application Result
                        </th>
                        <th className="px-3 py-2 text-right text-theme-muted font-semibold uppercase tracking-wider">
                          Count
                        </th>
                        <th className="px-3 py-2 text-right text-theme-muted font-semibold uppercase tracking-wider">
                          Share
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {bodBreakdown.breakdown.map((item) => {
                        const share =
                          bodBreakdown.total > 0
                            ? ((item.count / bodBreakdown.total) * 100).toFixed(
                                0,
                              )
                            : "—";
                        const textColor = item.isApproved
                          ? "text-status-green font-semibold"
                          : item.isDeclined || item.isArchived
                            ? "text-status-red"
                            : item.isWithdrew
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-theme-muted";
                        return (
                          <tr
                            key={item.result}
                            className="hover:bg-theme-overlay/50 transition-colors"
                          >
                            <td
                              className={`px-3 py-2 font-medium ${textColor}`}
                            >
                              {item.result}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold text-theme-primary">
                              {item.count.toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-theme-muted">
                              {share}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Explanatory note */}
              <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 px-4 py-3">
                <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                  <span className="font-semibold">
                    Where does the large Training drop-off come from?
                  </span>{" "}
                  Of the {bodBreakdown.total.toLocaleString()} applicants who
                  reached the BOD stage, only{" "}
                  {bodBreakdown.approvedCount.toLocaleString()} were approved,
                  and {bodBreakdown.inTraining.toLocaleString()} have a training
                  record. The gap between approved and in-training represents
                  applicants voted in but not yet assigned to a training cohort
                  — not a silent attrition event. The remaining drop is
                  explained by the result breakdown above (declined, withdrew,
                  archived, or pending).
                </p>
              </div>
            </div>
          )}
        </ChartCard>
      </section>

      {/* ── Section 4: Stage Detail Drill-Down ───────────────────────── */}
      <section>
        <ChartCard
          title="Stage Detail Drill-Down"
          subtitle="Select a stage to see detailed analytics, persona mix, and suggested actions"
          minHeight={120}
        >
          {waterfallLoading ? (
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="skeleton h-8 w-24 rounded-full" />
              ))}
            </div>
          ) : (
            <div>
              <StageSelector
                steps={steps}
                selected={selectedStage}
                onSelect={setSelectedStage}
              />
              {selectedStage !== null && (
                <StageDetailPanel stageNum={selectedStage} />
              )}
              {selectedStage === null && (
                <p className="text-xs text-theme-muted mt-3">
                  Click a stage above to see applicant detail, persona mix,
                  projected timelines, and recommended next actions.
                </p>
              )}
            </div>
          )}
        </ChartCard>
      </section>
    </div>
  );
}
